import React, { FC, ReactNode, useState } from 'react';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

import { Carousel } from '@mantine/carousel';
import { Box, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { COMPONENT_IMAGE_TYPE_OPTIONS, Component, File } from 'models';

import {
    ComponentDatasheetFallbackImage,
    ComponentDatasheetHeaderImages,
} from 'components/component-datasheet/components/ComponentDatasheetHeaderImages';
import { IKImage } from 'components/ik-image/IKImage';
import { useIsMobile } from 'hooks/use-is-mobile';

import cx from './Header.Images.module.scss';

const HeaderImages: FC<{
    images: Component['images'];
    manufacturer?: Component['manufacturer'];
    componentType?: Component['type'];
    actions?: ReactNode;
}> = ({ images: passedImages, manufacturer, componentType, actions }) => {
    const isMobile = useIsMobile('sm');
    const height = isMobile ? 200 : 400;

    const images = (passedImages || []).filter((image) => Boolean(image.file));

    const [activeIndex, setActiveIndex] = useState(0);
    const [fullscreen, handlers] = useDisclosure();

    const openFullscreen = (index: number) => {
        setActiveIndex(index);
        handlers.open();
    };

    if (images.length === 0) {
        return (
            <Box className={cx.card}>
                <ComponentDatasheetFallbackImage
                    manufacturer={manufacturer}
                    componentType={componentType}
                    height={height}
                />
                {actions && <Box className={cx.actions}>{actions}</Box>}
            </Box>
        );
    }

    return (
        <Box className={cx.card}>
            <Carousel
                slideGap="sm"
                withControls={images.length > 1}
                previousControlIcon={<IoChevronBack size={18} />}
                nextControlIcon={<IoChevronForward size={18} />}
                classNames={{ controls: cx.controls, control: cx.control, slide: cx.slide }}
                onSlideChange={setActiveIndex}
            >
                {images.map((image, index) => (
                    <Carousel.Slide key={index} onClick={() => openFullscreen(index)}>
                        <IKImage
                            fileOrId={image.file as File | string}
                            width={isMobile ? 600 : 800}
                            h={height}
                            fit="contain"
                            alt={COMPONENT_IMAGE_TYPE_OPTIONS.find((option) => option.value === image.type)?.label}
                        />
                    </Carousel.Slide>
                ))}
            </Carousel>

            {images.length > 1 && (
                <Box className={cx.counter}>
                    {activeIndex + 1} / {images.length}
                </Box>
            )}

            {actions && <Box className={cx.actions}>{actions}</Box>}

            <Modal opened={fullscreen} onClose={handlers.close} withCloseButton={false} size="xl">
                <ComponentDatasheetHeaderImages images={images} imageHeight={600} showType initialSlide={activeIndex} />
            </Modal>
        </Box>
    );
};

export { HeaderImages };
